import { useEffect, useState } from "react";
import { t } from "../i18n";
import { LangButton } from "./LangButton";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Home, Users, Calendar, Trophy, MoreHorizontal, Lock, Plus, ChevronLeft, CalendarPlus, PenLine, X, Bell, Contact } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useModals } from "../context/ModalsContext";

const TABS = [
  { to: "/", label: "tabs.inicio", Icon: Home, id: "tab-inicio" },
  { to: "/personas", label: "tabs.personas", Icon: Users, id: "tab-personas" },
  { to: "/calendario", label: "tabs.calendario", Icon: Calendar, id: "tab-calendario" },
  { to: "/ranking", label: "tabs.ranking", Icon: Trophy, id: "tab-ranking" },
  { to: "/ajustes", label: "common.more", Icon: MoreHorizontal, id: "tab-mas" },
];

export function Shell({ title, back, children, fab = true, right }) {
  const { logout } = useAuth();
  const { openInteraction, openAppointment, version } = useModals();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: personId } = useParams();
  const [menu, setMenu] = useState(false);
  const [count, setCount] = useState(0);
  useEffect(() => { api.get("/reminders/count").then((r) => setCount(r.data.no_leidos)).catch(() => {}); }, [version, location.pathname]);
  const preset = personId && location.pathname.startsWith("/personas/") ? { person_ids: [personId] } : null;

  return (
    <div className="min-h-[100dvh] flex flex-col max-w-[480px] mx-auto bg-background sm:border-x sm:border-border">
      <header className="sticky top-0 z-[1050] backdrop-blur-md bg-background/85 border-b border-border px-4 pt-[env(safe-area-inset-top)]">
        <div className="h-14 flex items-center gap-2">
          {back ? (
            <button data-testid="header-back-btn" onClick={() => navigate(back === true ? -1 : back)} className="p-2 -ml-2 rounded-full hover:bg-secondary">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <span className="w-6 h-6 rounded-full border-[3px] border-[#d4af37] inline-block mr-1" aria-hidden />
          )}
          <h1 data-testid="header-title" className="flex-1 text-lg font-medium truncate">{title}</h1>
          {right}
          <LangButton />
          <Link to="/alertas" data-testid="header-bell-btn" aria-label={t("alerts.title")} className="relative p-2 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
            <Bell size={20} />
            {count > 0 && <span data-testid="bell-badge" className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d4af37] text-[#0b0c0e] text-[10px] font-semibold flex items-center justify-center">{count > 99 ? "99+" : count}</span>}
          </Link>
          <button data-testid="header-lock-logout-btn" onClick={logout} title={t("common.logout")} aria-label={t("common.logout")}
            className="p-2 -mr-2 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
            <Lock size={20} />
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 pt-4 pb-28">{children}</main>

      {fab && location.pathname !== "/personas/nueva" && (
        <div className="fixed bottom-[calc(76px+env(safe-area-inset-bottom))] right-[max(16px,calc(50%-224px))] z-[1050] flex flex-col items-end gap-2">
          {menu && (
            <div className="flex flex-col items-end gap-2 fade-up" data-testid="fab-menu">
              <button data-testid="fab-new-interaction" onClick={() => { setMenu(false); openInteraction(preset); }} className="flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5 text-sm shadow-lg"><PenLine size={16} className="text-[#d4af37]" /> {t("interaction.new")}</button>
              <button data-testid="fab-new-appointment" onClick={() => { setMenu(false); openAppointment(preset); }} className="flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5 text-sm shadow-lg"><CalendarPlus size={16} className="text-[#d4af37]" /> {t("appt.new")}</button>
              <Link to="/personas/nueva" data-testid="fab-new-person" onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5 text-sm shadow-lg"><Users size={16} className="text-[#d4af37]" /> {t("person.new")}</Link>
              <Link to="/personas/nueva?importar=1" data-testid="fab-import-contact" onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5 text-sm shadow-lg"><Contact size={16} className="text-[#d4af37]" /> {t("fab.import")}</Link>
            </div>
          )}
          <button data-testid="fab-add-btn" aria-label={t("common.add")} onClick={() => setMenu((m) => !m)}
            className="w-14 h-14 rounded-full bg-[#d4af37] text-[#0b0c0e] flex items-center justify-center shadow-[0_0_16px_rgba(212,175,55,0.35)] active:scale-95 transition-transform">
            {menu ? <X size={26} /> : <Plus size={26} />}
          </button>
        </div>
      )}

      <nav data-testid="bottom-tabbar" className="fixed bottom-0 inset-x-0 z-[1050] max-w-[480px] mx-auto border-t border-border backdrop-blur-md bg-background/90 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 h-16">
          {TABS.map(({ to, label, Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={id}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] transition-colors ${isActive ? "text-[#f3c649]" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon size={22} strokeWidth={1.8} />
              <span>{t(label)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
